import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { TrashIcon } from "@heroicons/react/24/outline"
import { Button, Input, Select } from "~/ui-rtk/components/ui/controls"
import { type TSelectOption } from "~/ui-rtk/components/ui/controls/Select"
import {
  useBudgetUpdateCadenceControllerGetCadenceQuery,
  useBudgetUpdateCadenceControllerUpsertCadenceMutation,
} from "~/ui-rtk/api/budgetUpdateCadenceApi"
import { TIMEZONES } from "~/ui-rtk/pages/Settings/CompanyPage/timezones"
import { toCurrency } from "~/ui-rtk/utils/number-utils"
import { useUpdatesPerWeekRecommendation } from "./useUpdatesPerWeekRecommendation"

const MAX_UPDATES_PER_WEEK = 7

const DAY_OPTIONS: TSelectOption[] = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
]

const UPDATES_PER_WEEK_OPTIONS: TSelectOption[] = Array.from(
  { length: MAX_UPDATES_PER_WEEK },
  (_, i) => ({ label: `${i + 1}`, value: i + 1 }),
)

interface SlotForm {
  dayOfWeek: number
  timeOfDay: string
  timezone: string
}

const getBrowserTimezone = (): string => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    )
  } catch {
    return "America/New_York"
  }
}

const makeDefaultSlot = (timezone: string): SlotForm => ({
  dayOfWeek: 1,
  timeOfDay: "09:00",
  timezone,
})

const BudgetUpdatesPage: React.FC = () => {
  const browserTimezone = useMemo(getBrowserTimezone, [])

  const timezoneOptions = useMemo<TSelectOption[]>(
    () =>
      TIMEZONES.map(({ timezone }) => ({ label: timezone, value: timezone })),
    [],
  )

  const { data: cadence, isLoading: isLoadingCadence } =
    useBudgetUpdateCadenceControllerGetCadenceQuery()
  const [upsertCadence, { isLoading: isSaving }] =
    useBudgetUpdateCadenceControllerUpsertCadenceMutation()

  const {
    recommendedUpdatesPerWeek,
    averageDailySpend,
    isLoading: isLoadingRecommendation,
  } = useUpdatesPerWeekRecommendation()

  const [slackHandle, setSlackHandle] = useState("")
  const [updatesPerWeek, setUpdatesPerWeek] = useState(1)
  const [isEnabled, setIsEnabled] = useState(true)
  const [slots, setSlots] = useState<SlotForm[]>([
    makeDefaultSlot(browserTimezone),
  ])
  const [hasLoaded, setHasLoaded] = useState(false)

  // Hydrate form from the saved cadence once it arrives.
  useEffect(() => {
    if (isLoadingCadence || hasLoaded) {
      return
    }
    if (cadence) {
      setSlackHandle(cadence.slackHandle ?? "")
      setUpdatesPerWeek(cadence.updatesPerWeek)
      setIsEnabled(cadence.isEnabled)
      setSlots(
        cadence.slots.length > 0
          ? cadence.slots.map(slot => ({
              dayOfWeek: slot.dayOfWeek,
              timeOfDay: slot.timeOfDay,
              timezone: slot.timezone,
            }))
          : [makeDefaultSlot(browserTimezone)],
      )
    }
    setHasLoaded(true)
  }, [cadence, isLoadingCadence, hasLoaded, browserTimezone])

  // Keep the number of slot rows in sync with the selected updates-per-week.
  const handleUpdatesPerWeekChange = (next: number) => {
    setUpdatesPerWeek(next)
    setSlots(prev => {
      if (next === prev.length) {
        return prev
      }
      if (next < prev.length) {
        return prev.slice(0, next)
      }
      const additions = Array.from({ length: next - prev.length }, () =>
        makeDefaultSlot(browserTimezone),
      )
      return [...prev, ...additions]
    })
  }

  const handleSlotChange = (
    index: number,
    field: keyof SlotForm,
    value: string | number,
  ) => {
    setSlots(prev =>
      prev.map((slot, i) => (i === index ? { ...slot, [field]: value } : slot)),
    )
  }

  const handleSave = async () => {
    try {
      await upsertCadence({
        upsertBudgetUpdateCadenceDto: {
          slackHandle: slackHandle.trim() || null,
          updatesPerWeek,
          isEnabled,
          slots: slots.map(slot => ({
            dayOfWeek: slot.dayOfWeek,
            timeOfDay: slot.timeOfDay,
            timezone: slot.timezone,
          })),
        },
      }).unwrap()
      toast.success("Budget update schedule saved")
    } catch {
      toast.error("Failed to save budget update schedule")
    }
  }

  if (isLoadingCadence) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white">Budget Updates</h1>
        <p className="text-sm text-gray-400 mt-1">
          Choose when you want to be reminded to review and apply budget
          updates. You&apos;ll get a Slack DM and an in-app notification linking
          straight to your recommended budget changes.
        </p>
      </div>

      <div className="flex flex-col gap-6">
        <section className="card bg-basic-dark-blue border border-basic-blue rounded-lg p-5">
          <h2 className="text-md font-bold text-white mb-1">
            How many budget updates do you want to make per week?
          </h2>
          <p className="text-sm text-gray-400 mb-3">
            {isLoadingRecommendation
              ? "Calculating a recommendation from your recent spend…"
              : `Based on an average of ${toCurrency(
                  averageDailySpend,
                )}/day over the last 30 days, we recommend ${recommendedUpdatesPerWeek} update${
                  recommendedUpdatesPerWeek > 1 ? "s" : ""
                } per week.`}
          </p>
          <div className="flex items-center gap-3">
            <Select
              className="w-32"
              options={UPDATES_PER_WEEK_OPTIONS}
              value={updatesPerWeek}
              onChange={e => handleUpdatesPerWeekChange(Number(e.target.value))}
            />
            {!isLoadingRecommendation &&
              updatesPerWeek !== recommendedUpdatesPerWeek && (
                <Button
                  type="button"
                  variant={{ variant: "outlined" }}
                  className="p-0 h-auto"
                  onClick={() =>
                    handleUpdatesPerWeekChange(recommendedUpdatesPerWeek)
                  }
                >
                  <span className="text-white px-4 py-2 text-sm">
                    Use recommended ({recommendedUpdatesPerWeek})
                  </span>
                </Button>
              )}
          </div>
        </section>

        <section className="card bg-basic-dark-blue border border-basic-blue rounded-lg p-5">
          <h2 className="text-md font-bold text-white mb-3">Schedule</h2>
          <div className="flex flex-col gap-3">
            {slots.map((slot, index) => (
              <div
                key={index}
                className="flex flex-wrap items-end gap-3 pb-3 border-b border-basic-blue/40 last:border-b-0"
              >
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-400">Day</span>
                  <Select
                    className="w-40"
                    options={DAY_OPTIONS}
                    value={slot.dayOfWeek}
                    onChange={e =>
                      handleSlotChange(
                        index,
                        "dayOfWeek",
                        Number(e.target.value),
                      )
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-400">Time</span>
                  <input
                    type="time"
                    className="select w-32 bg-basic-dark-blue border border-basic-blue"
                    value={slot.timeOfDay}
                    onChange={e =>
                      handleSlotChange(index, "timeOfDay", e.target.value)
                    }
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-400">Timezone</span>
                  <Select
                    className="w-64"
                    options={timezoneOptions}
                    value={slot.timezone}
                    onChange={e =>
                      handleSlotChange(index, "timezone", e.target.value)
                    }
                  />
                </div>
                {slots.length > 1 && (
                  <button
                    type="button"
                    className="text-gray-400 hover:text-red-500 transition-colors p-2 mb-1"
                    onClick={() => {
                      setSlots(prev => prev.filter((_, i) => i !== index))
                      setUpdatesPerWeek(prev => Math.max(1, prev - 1))
                    }}
                    aria-label="Remove schedule slot"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {slots.length < MAX_UPDATES_PER_WEEK && (
            <Button
              type="button"
              variant={{ variant: "outlined" }}
              className="p-0 h-auto mt-3 self-start"
              onClick={() => {
                setSlots(prev => [...prev, makeDefaultSlot(browserTimezone)])
                setUpdatesPerWeek(prev =>
                  Math.min(MAX_UPDATES_PER_WEEK, prev + 1),
                )
              }}
            >
              <span className="text-white px-4 py-2 text-sm">+ Add a time</span>
            </Button>
          )}
        </section>

        <section className="card bg-basic-dark-blue border border-basic-blue rounded-lg p-5">
          <h2 className="text-md font-bold text-white mb-3">Notifications</h2>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1 max-w-sm">
              <span className="text-xs text-gray-400">Slack username</span>
              <Input
                fullWidth
                placeholder="e.g. jane.doe"
                value={slackHandle}
                onChange={e => setSlackHandle(e.target.value)}
              />
              <span className="text-xs text-gray-500">
                We deliver your Slack DM using your account email; this handle
                is stored for reference.
              </span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={isEnabled}
                onChange={e => setIsEnabled(e.target.checked)}
              />
              <span className="text-sm text-white">
                Enable budget update reminders
              </span>
            </label>
          </div>
        </section>

        <div className="flex justify-end">
          <Button
            type="button"
            disabled={isSaving}
            variant={{ variant: "solid", color: "pink" }}
            className="p-0 h-auto"
            onClick={handleSave}
          >
            <span className="text-white px-6 py-2 text-sm">
              {isSaving ? "Saving…" : "Save"}
            </span>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default BudgetUpdatesPage
